import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import type { League, Player } from '../../../sdk/model';
import {
  buildLeagueRosterCapSummaryRows,
  getPositionChipDisplay,
  SKILL_POSITION_DEPTH_THRESHOLDS,
  type TeamRosterCapSummaryRow,
} from '../../util/leagueRosterSummary';
import { toSalary } from '../../util/SalaryDisplay';

interface LeagueRosterCapSummaryProps {
  league: League;
  userId: string | undefined;
  playerMap: Map<string | undefined, Player | undefined>;
  /** When false, omits the page title block (used on the dedicated page). */
  showIntro?: boolean;
}

function PositionSlotChips({ row }: { row: TeamRosterCapSummaryRow }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {row.positionSlots.map((slot) => {
        const display = getPositionChipDisplay(slot.label, slot.filled, slot.max);
        const depthThreshold = SKILL_POSITION_DEPTH_THRESHOLDS[slot.label];

        let tooltip: string;
        let chipSx: Record<string, unknown> = { fontSize: '0.7rem' };
        let variant: 'filled' | 'outlined' = 'outlined';
        let color: 'default' | 'success' = 'default';

        switch (display) {
          case 'empty':
            tooltip = `No ${slot.label} players on roster`;
            variant = 'filled';
            color = 'error';
            break;
          case 'exact':
            tooltip = `${slot.filled} players at ${slot.label} — exactly meets the ${slot.max} roster spot requirement`;
            variant = 'filled';
            color = 'success';
            chipSx = {
              ...chipSx,
              bgcolor: theme.palette.success.dark,
              color: theme.palette.success.contrastText,
            };
            break;
          case 'plusOne':
            tooltip = `${slot.filled} players at ${slot.label} — at least one more than required (${slot.max} spots)`;
            variant = 'filled';
            color = 'success';
            break;
          case 'deep':
            tooltip =
              depthThreshold !== undefined
                ? `${slot.filled} players at ${slot.label} — more than ${depthThreshold} (strong ${slot.label} depth)`
                : `${slot.filled} players at ${slot.label} — well above roster requirement`;
            chipSx = {
              ...chipSx,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.text.primary,
              borderColor: alpha(theme.palette.success.main, 0.35),
            };
            break;
          default:
            tooltip = `${slot.filled} players at ${slot.label}, ${slot.open} open (${slot.max} roster spots allowed)`;
            break;
        }

        return (
          <Tooltip key={slot.label} title={tooltip}>
            <Chip
              size="small"
              variant={variant}
              color={color}
              label={`${slot.label} ${slot.filled}/${slot.max}`}
              sx={chipSx}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

type SortField = 'cap' | 'roster';
type SortDirection = 'asc' | 'desc';

function compareCapRemaining(a: TeamRosterCapSummaryRow, b: TeamRosterCapSummaryRow): number {
  const aCap = a.capRemaining ?? Number.NEGATIVE_INFINITY;
  const bCap = b.capRemaining ?? Number.NEGATIVE_INFINITY;
  return aCap - bCap;
}

function sortRows(
  rows: TeamRosterCapSummaryRow[],
  sortField: SortField | null,
  sortDirection: SortDirection
): TeamRosterCapSummaryRow[] {
  if (!sortField) {
    return rows;
  }
  const sorted = [...rows].sort((a, b) => {
    const cmp =
      sortField === 'cap'
        ? compareCapRemaining(a, b)
        : a.openRosterSpots - b.openRosterSpots;
    return sortDirection === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

function CapSpaceCell({ row }: { row: TeamRosterCapSummaryRow }) {
  if (row.capRemaining === null) {
    return <Typography variant="body2">N/A</Typography>;
  }
  const color =
    row.capRemaining < 0 ? 'error.main' : row.capRemaining === 0 ? 'warning.main' : 'success.main';
  return (
    <Typography variant="body2" fontWeight="bold" color={color}>
      {row.capRemainingFormatted}
    </Typography>
  );
}

function LeagueRosterCapSummary({
  league,
  userId,
  playerMap,
  showIntro = true,
}: LeagueRosterCapSummaryProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const salaryCap = league.currentSeason?.nflSeason?.salaryCap;
  const [sortField, setSortField] = React.useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

  const rows = React.useMemo(
    () => buildLeagueRosterCapSummaryRows(league, userId, playerMap),
    [league, userId, playerMap]
  );

  const sortedRows = React.useMemo(
    () => sortRows(rows, sortField, sortDirection),
    [rows, sortField, sortDirection]
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No teams found for this league.
      </Typography>
    );
  }

  return (
    <Box>
      {showIntro && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Remaining salary cap and open roster spots for each team
          {salaryCap !== undefined ? ` (${toSalary(salaryCap)} league cap)` : ''}.
          Position chips count players by position across the whole roster (starters, flex, and
          bench). Red = none; dark green = exact requirement; green = one above required; light
          green = above depth threshold (QB &gt;2, RB &gt;3, WR &gt;4, TE &gt;2).
        </Typography>
      )}

      {isMobile ? (
        <Stack spacing={2}>
          {sortedRows.map((row) => (
            <Box
              key={row.teamId}
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: row.isUserTeam ? 'primary.main' : 'divider',
                bgcolor: row.isUserTeam ? 'action.hover' : 'background.paper',
              }}
            >
              <Link
                component="button"
                variant="subtitle1"
                fontWeight={row.isUserTeam ? 'bold' : 'medium'}
                onClick={() => navigate(`/league/${league.id}/team/${row.teamId}`)}
                sx={{ textAlign: 'left', mb: 1 }}
              >
                {row.teamName}
                {row.isUserTeam ? ' (Your team)' : ''}
              </Link>
              <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccountBalanceWalletIcon fontSize="small" color="action" />
                  <CapSpaceCell row={row} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <GroupsIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium">
                    {row.openRosterSpots} open ({row.filledRosterSpots}/{row.maxRosterSpots})
                  </Typography>
                </Box>
              </Stack>
              <PositionSlotChips row={row} />
            </Box>
          ))}
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell align="right" sortDirection={sortField === 'cap' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'cap'}
                    direction={sortField === 'cap' ? sortDirection : 'asc'}
                    onClick={() => handleSort('cap')}
                  >
                    Cap space left
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={sortField === 'roster' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'roster'}
                    direction={sortField === 'roster' ? sortDirection : 'asc'}
                    onClick={() => handleSort('roster')}
                  >
                    Open roster spots
                  </TableSortLabel>
                </TableCell>
                <TableCell>Players by position</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow
                  key={row.teamId}
                  sx={row.isUserTeam ? { bgcolor: 'action.hover' } : undefined}
                >
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      fontWeight={row.isUserTeam ? 'bold' : 'regular'}
                      onClick={() => navigate(`/league/${league.id}/team/${row.teamId}`)}
                    >
                      {row.teamName}
                    </Link>
                  </TableCell>
                  <TableCell align="right">
                    <CapSpaceCell row={row} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {row.openRosterSpots}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.filledRosterSpots} / {row.maxRosterSpots} filled
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <PositionSlotChips row={row} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default LeagueRosterCapSummary;
